import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Trash2, Eye, EyeOff, Check, X } from 'lucide-react';
import { authAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const USERNAME_RE = /^[a-zA-Z0-9_.]{3,30}$/;

export default function AccountSettingsPage() {
  const { user, loading: authLoading, logout, checkAuth } = useAuth();
  const navigate = useNavigate();

  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const [username, setUsername] = useState('');
  const [usernameStatus, setUsernameStatus] = useState('idle'); // idle | checking | available | taken | invalid
  const [savingUsername, setSavingUsername] = useState(false);

  const [phone, setPhone] = useState('');
  const [phoneStep, setPhoneStep] = useState('phone'); // phone | code
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneInfo, setPhoneInfo] = useState('');

  useEffect(() => {
    if (!authLoading && !user) navigate('/auth');
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user?.username) setUsername(user.username);
    if (user?.phone) setPhone(user.phone);
  }, [user?.username, user?.phone]);

  useEffect(() => {
    if (!username || username === user?.username) { setUsernameStatus('idle'); return; }
    if (!USERNAME_RE.test(username)) { setUsernameStatus('invalid'); return; }
    setUsernameStatus('checking');
    const timer = setTimeout(async () => {
      try {
        const resp = await authAPI.checkUsername(username);
        setUsernameStatus(resp.data.available ? 'available' : 'taken');
      } catch {
        setUsernameStatus('idle');
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [username, user?.username]);

  const saveUsername = async () => {
    if (usernameStatus !== 'available') return;
    setSavingUsername(true);
    try {
      await authAPI.updateProfile({ username });
      await checkAuth();
      toast.success('Username updated.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save username.');
    } finally {
      setSavingUsername(false);
    }
  };

  const sendPhoneCode = async () => {
    if (!phone.trim()) { toast.error('Enter a phone number first.'); return; }
    setPhoneLoading(true);
    try {
      await authAPI.sendPhoneCode(phone.trim());
      setPhoneStep('code');
      setPhoneInfo('We sent a 6-digit code to your phone.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send verification code.');
    } finally {
      setPhoneLoading(false);
    }
  };

  const verifyPhoneCode = async () => {
    if (phoneCode.length < 4) { toast.error('Enter the code you received.'); return; }
    setPhoneLoading(true);
    try {
      await authAPI.verifyPhoneCode(phone.trim(), phoneCode.trim());
      await checkAuth();
      setPhoneStep('phone');
      setPhoneCode('');
      toast.success('Phone number verified.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid or expired code.');
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!password) { setError('Please enter your password to confirm.'); return; }
    setDeleting(true);
    setError('');
    try {
      await authAPI.deleteAccount({ password });
      toast.success('Your account has been deleted.');
      await logout();
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not delete account.');
    } finally {
      setDeleting(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#050505] pt-16 page-enter" data-testid="account-settings-page">
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-8">
        <h1 className="text-3xl font-black uppercase tracking-tight text-white mb-8" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Account Settings
        </h1>

        <div className="bg-[#0A0A0A] border border-gray-800 rounded-xl p-6 mb-6">
          <h2 className="text-white font-bold text-sm uppercase tracking-wide mb-4">Profile</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between border-b border-gray-800 pb-2">
              <span className="text-gray-500">Name</span>
              <span className="text-white">{user.name}</span>
            </div>
            <div className="flex justify-between border-b border-gray-800 pb-2">
              <span className="text-gray-500">Email</span>
              <span className="text-white">{user.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Role</span>
              <span className="text-white capitalize">{user.is_dj ? 'DJ' : user.account_type === 'dj' ? 'DJ (pending approval)' : user.role}</span>
            </div>
          </div>
        </div>

        <div className="bg-[#0A0A0A] border border-gray-800 rounded-xl p-6 mb-6">
          <h2 className="text-white font-bold text-sm uppercase tracking-wide mb-4">Username</h2>
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-sm">sacevents.app/</span>
            <input
              value={username}
              onChange={e => setUsername(e.target.value.toLowerCase().replace(/\s/g, ''))}
              placeholder="yourname"
              className="flex-1 bg-[#111] border border-gray-700 text-white px-3 py-2 rounded text-sm"
              data-testid="username-input"
            />
            <button
              onClick={saveUsername}
              disabled={usernameStatus !== 'available' || savingUsername}
              className="px-4 py-2 rounded text-sm font-bold text-white bg-red-700 hover:bg-red-600 disabled:opacity-40 transition-colors"
            >
              {savingUsername ? 'Saving…' : 'Save'}
            </button>
          </div>
          {usernameStatus !== 'idle' && (
            <p className={`text-xs mt-2 flex items-center gap-1 ${
              usernameStatus === 'available' ? 'text-green-400' : usernameStatus === 'checking' ? 'text-gray-500' : 'text-red-400'
            }`}>
              {usernameStatus === 'available' && <Check size={12} />}
              {(usernameStatus === 'taken' || usernameStatus === 'invalid') && <X size={12} />}
              {{
                checking: 'Checking availability...',
                available: 'Username is available.',
                taken: 'Username is already taken.',
                invalid: 'Use 3-30 letters, numbers, dots or underscores.',
              }[usernameStatus]}
            </p>
          )}
        </div>

        <div className="bg-[#0A0A0A] border border-gray-800 rounded-xl p-6 mb-6">
          <h2 className="text-white font-bold text-sm uppercase tracking-wide mb-4">Phone Number</h2>
          {user.phone_verified_at ? (
            <div className="flex items-center justify-between text-sm">
              <span className="text-white flex items-center gap-2"><Check size={14} className="text-green-400" /> {user.phone}</span>
              <span className="text-green-400 text-xs uppercase font-bold">Verified</span>
            </div>
          ) : phoneStep === 'phone' ? (
            <div className="flex items-center gap-2">
              <input
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="+250 7XX XXX XXX"
                className="flex-1 bg-[#111] border border-gray-700 text-white px-3 py-2 rounded text-sm"
                data-testid="phone-input"
              />
              <button
                onClick={sendPhoneCode}
                disabled={phoneLoading}
                className="px-4 py-2 rounded text-sm font-bold text-white bg-red-700 hover:bg-red-600 disabled:opacity-40 transition-colors"
              >
                {phoneLoading ? 'Sending…' : 'Send code'}
              </button>
            </div>
          ) : (
            <div>
              <p className="text-gray-500 text-xs mb-2">{phoneInfo}</p>
              <div className="flex items-center gap-2">
                <input
                  value={phoneCode}
                  onChange={e => setPhoneCode(e.target.value)}
                  placeholder="123456"
                  maxLength={6}
                  className="flex-1 bg-[#111] border border-gray-700 text-white px-3 py-2 rounded text-sm"
                  data-testid="phone-code-input"
                />
                <button
                  onClick={verifyPhoneCode}
                  disabled={phoneLoading}
                  className="px-4 py-2 rounded text-sm font-bold text-white bg-red-700 hover:bg-red-600 disabled:opacity-40 transition-colors"
                >
                  {phoneLoading ? 'Verifying…' : 'Verify'}
                </button>
              </div>
              <button onClick={sendPhoneCode} disabled={phoneLoading} className="text-red-400 text-xs mt-2 hover:text-red-300">
                Resend code
              </button>
            </div>
          )}
        </div>

        <div className="bg-[#150808] border border-red-900/50 rounded-xl p-6" data-testid="danger-zone">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={16} className="text-red-500" />
            <h2 className="text-red-400 font-bold text-sm uppercase tracking-wide">Danger Zone</h2>
          </div>
          <p className="text-gray-400 text-sm mb-4">
            Deleting your account permanently removes your profile and discussion posts. Past ticket
            orders are kept for the event organizer's records but are no longer linked to you. This
            cannot be undone.
          </p>

          {!confirming ? (
            <button
              onClick={() => setConfirming(true)}
              className="flex items-center gap-2 text-sm font-bold text-red-400 border border-red-900/60 hover:bg-red-950/40 px-4 py-2.5 rounded transition-colors"
              data-testid="open-delete-account-btn"
            >
              <Trash2 size={14} /> Delete Account
            </button>
          ) : (
            <div className="space-y-3">
              <label className="text-gray-400 text-xs font-medium uppercase tracking-wider block">
                Enter your password to confirm
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full bg-[#111] border border-gray-700 text-white px-3 py-2.5 rounded text-sm pr-10"
                  placeholder="••••••••"
                  data-testid="delete-account-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              {error && <p className="text-red-400 text-xs">{error}</p>}
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => { setConfirming(false); setPassword(''); setError(''); }}
                  className="flex-1 py-2.5 border border-gray-700 rounded text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 py-2.5 rounded text-sm font-bold text-white bg-red-700 hover:bg-red-600 disabled:opacity-50 transition-colors"
                  data-testid="confirm-delete-account-btn"
                >
                  {deleting ? 'Deleting…' : 'Delete Permanently'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
